import React, { useState } from 'react';
import {
  levelWithoutYes, yesGainLabel, fmtAbsorption, YES_BLOCKED_REASONS, YES_WARNINGS,
} from '../../utils/yes';

const ArrowRight = () => (
  <svg className="cd-yes-arrow" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
  </svg>
);

const AlertIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 9v4" /><path d="M12 17h.01" />
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
  </svg>
);

const YesIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 20v-8" />
    <path d="M12 12c0-3 2-5 6-5-1 4-3 5-6 5Z" />
    <path d="M12 14c0-2-1.6-3.6-5-3.6C7 13 9 14 12 14Z" />
  </svg>
);

const Chevron = () => (
  <svg className="cd-yes-chev" viewBox="0 0 24 24" aria-hidden="true">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// Pill shown in the hero level area. `level` is the post-YES scorecard level.
export const YesHeroBadge = ({ yes, level }) => {
  if (!yes) return null;
  const recognised = yes.qualifies_for_recognition;

  if (recognised) {
    const base = levelWithoutYes(level, yes.levels_gained);
    const gain = yesGainLabel(yes);
    return (
      <div className="cd-yes-hero">
        <span className="mk"><YesIcon /> Y.E.S</span>
        <span className="lvl">
          {base !== level ? (<><s>{base}</s><ArrowRight />{level}</>) : `${level} maintained`}
        </span>
        {gain && <span className="gain">{gain}</span>}
      </div>
    );
  }

  const reason = YES_BLOCKED_REASONS[yes.reason];
  if (!reason) return null;
  return (
    <div className="cd-yes-hero is-blocked">
      <span className="mk"><YesIcon /> Y.E.S</span>
      <span className="lvl">{reason}</span>
    </div>
  );
};

// Read-only scorecard card — collapsed to a one-line summary, click to reveal
// the level-impact detail. Placements and registration are managed by the consultant.
const YesCard = ({ yes, level }) => {
  const [open, setOpen] = useState(false);
  if (!yes) return null;

  const recognised = yes.qualifies_for_recognition;
  const base = levelWithoutYes(level, yes.levels_gained);
  const changed = recognised && base !== level;
  const reason = YES_BLOCKED_REASONS[yes.reason];

  let chip = 'No change';
  if (recognised) chip = 'Recognised';
  else if (yes.reason === 'not_registered') chip = 'Not registered';

  let summary;
  if (changed) summary = `${base} → ${level}`;
  else if (recognised) summary = `Level maintained with Y.E.S`;
  else summary = reason || 'No level impact yet';

  return (
    <div className={`cd-card cd-card--yes ${recognised ? 'is-ok' : 'is-blocked'} ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="cd-yes-toggle"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <div className="cd-yes-badge">YES</div>
        <div className="cd-card-head">
          <div className="nm"><span className="dot" /> Youth Employment Service</div>
          <div className="pts">{summary}</div>
        </div>
        <span className={`cd-chip ${recognised ? 'ok' : 'act'}`}>{chip}</span>
        <Chevron />
      </button>

      {open && (
        <div className="cd-yes-details">
          <div className={`cd-yes-impact ${changed ? '' : 'is-flat'}`}>
            {changed ? (
              <>
                <div className="lv from"><span>Scorecard level</span><b>{base}</b></div>
                <ArrowRight />
                <div className="lv to"><span>With Y.E.S</span><b>{level}</b></div>
              </>
            ) : (
              <div className="lv single"><span>Scorecard level</span><b>{level}</b></div>
            )}
          </div>

          {!recognised && reason && (
            <div className="cd-yes-reason">
              <AlertIcon />
              <p>{reason}. {yes.reason === 'not_registered'
                ? 'Speak to your consultant about registering to unlock a possible level bump.'
                : 'Your consultant can tell you what to change to earn a level.'}</p>
            </div>
          )}

          <div className="cd-yes-meta">
            {recognised && <div><span>Levels gained</span><b>+{yes.levels_gained}</b></div>}
            {recognised && <div><span>Bonus points</span><b>+{yes.bonus_points_gained}</b></div>}
            {yes.target > 0 && <div><span>Youth placed</span><b>{yes.actual} / {yes.target}</b></div>}
            {yes.absorption_pct != null && <div><span>Absorption</span><b>{fmtAbsorption(yes.absorption_pct)}</b></div>}
          </div>

          {yes.warnings?.length > 0 && (
            <ul className="cd-yes-warns">
              {yes.warnings.map((w) => <li key={w}>{YES_WARNINGS[w] || w}</li>)}
            </ul>
          )}

          <p className="cd-yes-note">
            Managed by your consultant — placements and registration are captured on their side.
          </p>
        </div>
      )}
    </div>
  );
};

export default YesCard;
